/**
 * @file orders.tsx
 * @module app/modules/sales-management/admin/orders/orders
 * @description Implements the orders interface for sales-management operations.
 *
 * @author Rocket Technologies Ltd
 * @copyright 2026 Rocket Technologies Ltd. All rights reserved.
 * @since 1.1.1
 * @version 2.1.2
 */

import { useEffect, useState } from "react";
import {
  Button,
  Card,
  Col,
  FormLabel,
  OverlayTrigger,
  Popover,
  Row,
} from "react-bootstrap";
import APICallService from "../../../../../api/apiCallService";
import { assistAdmin, ordersDelivery } from "../../../../../api/apiEndPoints";
import {
  Manager,
  ORDER_PAGE_LIMIT,
  SalesRepOrders,
  ServiceMember,
  OrdersDelivery,
  View,
  OrderPlacedByCustomer,
  RouteOrderMovedToPendingOrder,
  OrderSubmitted,
  OrderProcessed,
  OrderOutForDelivery,
  OrderDelivered,
  OrderCancelled,
  AssistAdmin,
  AllCustomers,
} from "../../../../../utils/constants";
import { getKey, removeKey, setKey } from "../../../../../Global/history";
import { listSalesRepOrdersDelivery } from "../../../../../utils/storeString";
import { useAuth } from "../../../auth";
import Loader from "../../../../../Global/loader";
import OrderPagination from "../../../../../Global/order-pagination";
import { useNavigate } from "react-router-dom";
import Method from "../../../../../utils/methods";
import { IMAGES } from "../../../../../utils/assetsAndPermissions";
import { KTSVG } from "../../../../../_admin/helpers";
import CustomDatePicker from "../../../../custom/DateRange/CustomDatePicker";
import { CustomSelect } from "../../../../custom/Select/CustomSelect";
import { orderStatusJSON } from "../../../../../utils/staticJSON";

const Orders = () => {
  const { currentUser }: any = useAuth();
  const [currentUserData, setCurrentUserData] = useState<any>();
  const navigate = useNavigate();
  const kgToTons = (kilograms: any) => {
    return (kilograms / 1000).toFixed(3);
  };
  const getStatusBadge = (status: any) => {
    const statusObj = orderStatusJSON.find((val) => val.value === status);
    if (!statusObj)
      return (
        <div className="badge rounded-pill bg-light-secondary text-secondary fs-14 p-4">
          NA
        </div>
      );

    let badgeClass = "bg-light-primary text-primary";
    switch (status) {
      case OrderProcessed:
        badgeClass = "bg-light-warning text-warning";
        break;
      case OrderOutForDelivery:
        badgeClass = "bg-light-info text-info";
        break;
      case OrderDelivered:
        badgeClass = "bg-light-success text-success";
        break;
      case OrderCancelled:
        badgeClass = "bg-light-danger text-danger";
        break;
      default:
        badgeClass = "bg-light-primary text-primary";
    }

    return (
      <div className={`badge rounded-pill ${badgeClass} fs-14 py-2 px-4`}>
        {statusObj.label}
      </div>
    );
  };
  const popoverPartialOrder = (
    <Popover id="popover-basic">
      <Popover.Body>This order is partially processed.</Popover.Body>
    </Popover>
  );
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState<any>([]);
  const [totalRecords, setTotalRecords] = useState(0);
  const [fetchLoader, setFetchLoader] = useState(true);
  const [page, setPage] = useState<any>(
    getKey(listSalesRepOrdersDelivery.page) || 1
  );
  const [pageLimit, setPageLimit] = useState<any>(
    getKey(listSalesRepOrdersDelivery.limit) || ORDER_PAGE_LIMIT
  );
  const [status, setStatus] = useState<any>(
    getKey(listSalesRepOrdersDelivery.status)
      ? Number(getKey(listSalesRepOrdersDelivery.status))
      : null
  );
  const [selectedManager, setSelectedManager] = useState<any>(
    getKey(listSalesRepOrdersDelivery.manager) || undefined
  );
  const [selectedRep, setSelectedRep] = useState<any>(
    getKey(listSalesRepOrdersDelivery.rep) || undefined
  );
  const [managerOptions, setManagerOptions] = useState<any[]>([]);
  const [repOptions, setRepOptions] = useState<any[]>([]);
  const [managerLoading, setManagerLoading] = useState<boolean>(false);
  const [repLoading, setRepLoading] = useState<boolean>(false);
  const districts =
    currentUser && currentUser.districts
      ? currentUser.districts.map((val: any) => val.reference)
      : [];
  const [startDate, setStartDate] = useState<any>(
    getKey(listSalesRepOrdersDelivery.filterStartDate)
      ? new Date(
        getKey(listSalesRepOrdersDelivery.filterStartDate)?.toString() || ""
      )
      : null
  );
  const [endDate, setEndDate] = useState<any>(
    getKey(listSalesRepOrdersDelivery.filterEndDate)
      ? new Date(
        getKey(listSalesRepOrdersDelivery.filterEndDate)?.toString() || ""
      )
      : null
  );
  const [search, setSearch] = useState<any>(
    getKey(listSalesRepOrdersDelivery.search) || ""
  );
  useEffect(() => {
    (async () => {
      setFetchLoader(true);
      await fetchManagers();
      if (selectedManager) {
        await fetchReps(selectedManager);
      }
      await fetchOrdersList(
        page,
        pageLimit,
        status ? [status] : [],
        search,
        startDate,
        endDate,
        districts,
        selectedManager,
        selectedRep
      );
      setFetchLoader(false);
    })();
  }, []);

  const fetchManagers = async () => {
    setManagerLoading(true);
    let params: any = {
      pageNo: 1,
      limit: 0,
      needCount: false,
      sortKey: "name",
      sortOrder: 1,
      "role[0]": 1,
      "role[1]": 6,
    };
    const apiService = new APICallService(
      assistAdmin.list,
      params,
      "",
      "",
      "",
      "",
      "",
      ""
    );
    const response = await apiService.callAPI();
    if (response && response.records) {
      const options = response.records.map((val: any) => ({
        value: val._id,
        label: val.name,
        title: val.name,
        ...val,
      }));
      setManagerOptions(options);
    }
    setManagerLoading(false);
  };

  const fetchReps = async (managerId?: string) => {
    if (!managerId) {
      setRepOptions([]);
      setRepLoading(false);
      return;
    }
    setRepLoading(true);
    let params: any = {
      pageNo: 1,
      limit: 0,
      needCount: false,
      sortKey: "name",
      sortOrder: 1,
      "role[0]": 2,
      "role[1]": 7,
    };
    params.manager = managerId;
    const apiService = new APICallService(
      assistAdmin.list,
      params,
      "",
      "",
      "",
      "",
      "",
      ""
    );
    const response = await apiService.callAPI();
    if (response && response.records) {
      const options = response.records.map((val: any) => ({
        value: val._id,
        label: val.name,
        title: val.name,
        ...val,
      }));
      setRepOptions(options);
    } else {
      setRepOptions([]);
    }
    setRepLoading(false);
  };

  const fetchOrdersList = async (
    pageNo: number,
    limit: number,
    statusList?: any,
    search?: string,
    fromDate?: string,
    toDate?: string,
    districts?: any,
    managerId?: string,
    repId?: string
  ) => {
    setLoading(true);
    let params: any = {
      pageNo: pageNo,
      limit: limit,
      sortKey: "_createdAt",
      sortOrder: -1,
      needCount: true,
      searchTerm: search ? search : "",
      fromDate: fromDate ? Method.convertDateToFormat(fromDate, "YYYY-MM-DD") : "",
      toDate: toDate ? Method.convertDateToFormat(toDate, "YYYY-MM-DD") : "",
    };
    // if (districts && districts.length > 0) {
    //   districts.forEach((d: any, i: number) => {
    //     params[`district[${i}]`] = d;
    //   });
    // } else {
    //   params["district"] = "";
    // }
    if (statusList && statusList.length > 0) {
      statusList.map((val: any) => {
        params = { ...params, listType: val };
      });
    }
    if (managerId) {
      params.manager = managerId;
    }
    if (repId) {
      params["assignedTo[0]"] = repId;
    }
    let endpoint = ordersDelivery.listAssistAdmin;
    //  if (
    //    currentUser?.role.some(
    //      (obj: any) => obj.type === Manager || obj.type === ServiceMember
    //    )
    //  ) {
    //    endpoint = ordersDelivery.listAssistAdmin;
    //  }
    let apiService = new APICallService(
      endpoint,
      params,
      "",
      "",
      "",
      "",
      SalesRepOrders
    );
    // Call API using the passed fromDate/toDate parameters. Do not rely on stale `currentUserData` state
    // The API will return records according to params (empty from/to -> server handles accordingly)
    let response: any = await apiService.callAPI();
    if (response && response.records) {
      setTotalRecords(response.total || 0);
      setOrders(response.records);
    } else {
      setTotalRecords(0);
      setOrders([]);
    }
    setLoading(false);
  };
  const handleCurrentPage = async (val: number) => {
    setFetchLoader(true);
    setPage(val);
    setKey(listSalesRepOrdersDelivery.page, val);
    await fetchOrdersList(
      val,
      pageLimit,
      status ? [status] : [],
      search,
      startDate,
      endDate,
      districts,
      selectedManager,
      selectedRep
    );
    setFetchLoader(false);
  };
  const handleNextPage = async (val: number) => {
    setFetchLoader(true);
    setPage(val + 1);
    setKey(listSalesRepOrdersDelivery.page, val + 1);
    await fetchOrdersList(
      val + 1,
      pageLimit,
      status ? [status] : [],
      search,
      startDate,
      endDate,
      districts,
      selectedManager,
      selectedRep
    );
    setFetchLoader(false);
  };
  const handlePreviousPage = async (val: number) => {
    setFetchLoader(true);
    setPage(val - 1);
    setKey(listSalesRepOrdersDelivery.page, val - 1);
    await fetchOrdersList(
      val - 1,
      pageLimit,
      status ? [status] : [],
      search,
      startDate,
      endDate,
      districts,
      selectedManager,
      selectedRep
    );
    setFetchLoader(false);
  };
  const handlePageLimit = async (event: any) => {
    const limit = parseInt(event.target.value);
    setFetchLoader(true);
    setPage(1);
    setPageLimit(limit);
    setKey(listSalesRepOrdersDelivery.page, 1);
    setKey(listSalesRepOrdersDelivery.limit, limit);
    await fetchOrdersList(
      1,
      limit,
      status ? [status] : [],
      search,
      startDate,
      endDate,
      districts,
      selectedManager,
      selectedRep
    );
    setFetchLoader(false);
  };
  const handleDateFilter = async (event: any) => {
    let start = event && event[0] ? event[0] : null;
    let end = event && event[1] ? event[1] : null;

    setStartDate(start);
    setEndDate(end);
    setKey(listSalesRepOrdersDelivery.filterStartDate, start, false);
    setKey(listSalesRepOrdersDelivery.filterEndDate, end, false);

    if (start && end) {
      setFetchLoader(true);
      setPage(1);
      setKey(listSalesRepOrdersDelivery.page, 1);
      await fetchOrdersList(
        1,
        pageLimit,
        status ? [status] : [],
        search,
        start,
        end,
        districts,
        selectedManager,
        selectedRep
      );
      setFetchLoader(false);
    } else {
      setFetchLoader(true);
      setPage(1);
      setKey(listSalesRepOrdersDelivery.page, 1);
      await fetchOrdersList(
        1,
        pageLimit,
        status ? [status] : [],
        search,
        '',
        '',
        districts,
        selectedManager,
        selectedRep
      );
      setFetchLoader(false);

    }
  };
  const handleSearch = async (value: any) => {
    setSearch(value);
    setFetchLoader(true);
    setPage(1);
    setKey(listSalesRepOrdersDelivery.search, value, false);
    setKey(listSalesRepOrdersDelivery.page, 1);
    await fetchOrdersList(
      1,
      pageLimit,
      status ? [status] : [],
      value,
      startDate,
      endDate,
      districts,
      selectedManager,
      selectedRep
    );
    setFetchLoader(false);
  };
  const handleStatusFilter = async (value: any) => {
    setStatus(value);
    setFetchLoader(true);
    setKey(listSalesRepOrdersDelivery.status, value);
    setKey(listSalesRepOrdersDelivery.page, 1);
    setLoading(true);
    setPage(1);
    await fetchOrdersList(
      1,
      pageLimit,
      value ? [value] : [],
      search,
      startDate,
      endDate,
      districts,
      selectedManager,
      selectedRep
    );
    setFetchLoader(false);
  };

  const handleManagerFilter = async (value: any) => {
    const managerId = value || undefined;
    setSelectedManager(managerId);
    setKey(listSalesRepOrdersDelivery.manager, managerId || "", false);
    // Reset rep when manager changes
    removeKey(listSalesRepOrdersDelivery.rep);
    setSelectedRep("");
    setRepOptions([]);
    await fetchReps(managerId);
    setFetchLoader(true);
    setPage(1);
    setKey(listSalesRepOrdersDelivery.page, 1);
    await fetchOrdersList(
      1,
      pageLimit,
      status ? [status] : [],
      search,
      startDate,
      endDate,
      districts,
      managerId,
      undefined
    );
    setFetchLoader(false);
  };

  const handleRepFilter = async (value: any) => {
    const repId = value || undefined;
    setSelectedRep(repId);
    setKey(listSalesRepOrdersDelivery.rep, repId || "", false);
    setFetchLoader(true);
    setPage(1);
    setKey(listSalesRepOrdersDelivery.page, 1);
    await fetchOrdersList(
      1,
      pageLimit,
      status ? [status] : [],
      search,
      startDate,
      endDate,
      districts,
      selectedManager,
      repId
    );
    setFetchLoader(false);
  };
  return (
    <div className="p-5">
      <Row className="align-items-center mb-7 g-5">
        <Col xs>
          <div className="d-flex justify-content-between">
            <h1 className="fs-22 fw-bolder">Orders</h1>
            {/* <Button
              variant="primary"
              className="fs-16 fw-bold btn-lg"
              onClick={() => {
                currentUser?.userType === AssistAdmin
                  ? currentUser?.role.some(
                    (obj: any) =>
                      obj.type === Manager || obj.type === ServiceMember
                  )
                    ? navigate("/orders/add-new-order-assistAdmin")
                    : navigate("/orders/add-new-order")
                  : navigate("/orders/add-new-order");
                setKey(listSalesRepOrdersDelivery.search, search, false);
                setKey(listSalesRepOrdersDelivery.filterStartDate, startDate, false);
                setKey(listSalesRepOrdersDelivery.filterEndDate, endDate, false);
              }}
            >
              Place Order
            </Button> */}
          </div>
          <Card className="bg-light border my-7">
            <Card.Body className="px-7">
              <Row className="align-items-center gy-5">
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500">
                    Search by Order Id / Customer Name
                  </FormLabel>
                  <div className="d-flex align-items-center position-relative">
                    <KTSVG
                      path="/media/icons/duotune/general/gen021.svg"
                      className="svg-icon-3 position-absolute ms-3"
                    />
                    <input
                      type="text"
                      id="kt_filter_search"
                      className="form-control form-control-custom borde-r8px bg-white ps-11"
                      placeholder="Search"
                      value={search}
                      onChange={(event: any) => {
                        handleSearch(event.target.value);
                      }}
                    />
                  </div>
                </Col>
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500">
                    Filter by status
                  </FormLabel>
                  <div>
                    <CustomSelect
                      backgroundColor="#ffff"
                      value={orderStatusJSON.filter(option => option.value !== 3).find(
                        (option) => option?.value == status
                      )}
                      options={orderStatusJSON.filter(option => option.value !== 3)}
                      onChange={(event: any) =>
                        handleStatusFilter(event ? event.value : undefined)
                      }
                      isSearchable={false}
                      isMulti={false}
                      isClearable={true}
                      placeholder="Select"
                    />
                  </div>
                </Col>
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500">Filter by date</FormLabel>
                  <CustomDatePicker
                    className="form-control bg-white min-h-60px fs-16 fw-bold text-dark min-w-175px"
                    onChange={handleDateFilter}
                    selectsRange={true}
                    startDate={startDate}
                    endDate={endDate}
                    dateFormat="dd/MM/yyyy"
                    showFullMonthYearPicker={true}
                    maxDate={
                      currentUserData?.currentSalesTarget?.endDate
                        ? new Date(currentUserData?.currentSalesTarget?.endDate)
                        : new Date()
                    }
                    minDate={
                      currentUserData?.currentSalesTarget?.startDate
                        ? new Date(
                          currentUserData?.currentSalesTarget?.startDate
                        )
                        : undefined
                    }
                    isClearable={true}
                    inputTextBG="bg-white"
                  />
                </Col>
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500">
                    Filter by manager
                  </FormLabel>
                  <div>
                    <CustomSelect
                      backgroundColor="#ffff"
                      options={managerOptions}
                      value={managerOptions.find((o) => o.value == selectedManager)}
                      onChange={(event: any) =>
                        handleManagerFilter(event ? event.value : undefined)
                      }
                      isSearchable={true}
                      isMulti={false}
                      isClearable={true}
                      isLoading={managerLoading}
                      placeholder="Select Manager"
                    />
                  </div>
                </Col>
                <Col sm={12} md={6} lg={4}>
                  <FormLabel className="fs-16 fw-500">
                    Filter by sales rep
                  </FormLabel>
                  <div>
                    <CustomSelect
                      backgroundColor="#ffff"
                      options={repOptions}
                      value={selectedRep ? repOptions.find((o) => o.value == selectedRep) : null}
                      onChange={(event: any) =>
                        handleRepFilter(event ? event.value : undefined)
                      }
                      isSearchable={true}
                      isMulti={false}
                      isClearable={true}
                      isLoading={repLoading}
                      disabled={!selectedManager}
                      placeholder="Select Sales Rep"
                    />
                  </div>
                </Col>
              </Row>
            </Card.Body>
          </Card>
          {!fetchLoader ? (
            orders && orders.length ? (
              <>
                <Card className="border border-r10px">
                  <Card.Body className="p-0">
                    <div className="table-responsive">
                      <table className="table table-rounded table-row-bordered align-middle gs-7 gy-6 mb-0">
                        <thead>
                          <tr className="fw-bold fs-16 fw-600 text-dark border-bottom align-middle">
                            {/* {enableSelectAll ? (
                                                          <th
                                                            className="min-w-100px"
                                                            tabIndex={0}
                                                            rowSpan={1}
                                                            colSpan={1}
                                                          >
                                                            <div className="form-check form-check-sm form-check-custom form-check-solid">
                                                              <input
                                                                className="form-check-input h-30px w-30px"
                                                                type="checkbox"
                                                                value="1"
                                                                checked={
                                                                  selectLoading ||
                                                                  (selectedId.length >=
                                                                    totalRecords &&
                                                                    districtData.findIndex(
                                                                      (
                                                                        val: any,
                                                                        index: any
                                                                      ) => {
                                                                        return (
                                                                          val._id ==
                                                                          currentDistrict
                                                                        );
                                                                      }
                                                                    ) === 0)
                                                                }
                                                                onChange={(
                                                                  event: any
                                                                ) => {
                                                                  handleSelectAll();
                                                                }}
                                                              />
                                                              <label className="form-check-label fs-16 fw-600 text-dark">
                                                                Select all
                                                              </label>
                                                            </div>
                                                          </th>
                                                        ) : (
                                                          <th className="min-w-md-175px min-w-150px">
                                                            Order Date & ID
                                                          </th>
                                                        )} */}
                            <th className="min-w-md-175px min-w-150px">
                              Order Date & ID
                            </th>
                            <th className="min-w-md-200px min-w-125px">
                              Customer name
                            </th>
                            {currentUser?.role.length ? (
                              currentUser?.role.some(
                                (obj: any) =>
                                  obj.type !== Manager ||
                                  obj.type !== ServiceMember
                              ) ? (
                                <></>
                              ) : (
                                <th className="min-w-140px">Ordered by</th>
                              )
                            ) : (
                              <></>
                            )}
                            <th className="min-w-md-125px">Rep Name</th>
                            <th className="min-w-md-125px min-w-100px">
                              Total amount
                            </th>
                            <th className="min-w-md-75px min-w-50px">Status</th>
                            <th className="min-w-100px text-end"></th>
                          </tr>
                        </thead>
                        <tbody>
                          <>
                            {orders.map((orderVal: any, goodIndex: number) => (
                              <tr key={goodIndex}>
                                {/* {enableSelectAll ? (
                                                                  <td>
                                                                    <div className="form-check form-check-sm form-check-custom form-check-solid">
                                                                      <input
                                                                        className="form-check-input h-30px w-30px"
                                                                        type="checkbox"
                                                                        value={
                                                                          orderVal._id
                                                                        }
                                                                        checked={selectedId.includes(
                                                                          orderVal._id
                                                                        )}
                                                                        onChange={() => {
                                                                          handleChecked(
                                                                            orderVal._id
                                                                          );
                                                                        }}
                                                                      />
                                                                      <div className="d-flex align-items-start flex-column ms-2">
                                                                        <div className="d-flex flex-row">
                                                                          {orderVal.totalQuantities.reduce(
                                                                            (
                                                                              total: number,
                                                                              item: any
                                                                            ) =>
                                                                              total +
                                                                              item.stockCount,
                                                                            0
                                                                          ) >
                                                                            orderVal.mProcessedQuantities &&
                                                                          orderVal.mProcessedQuantities !=
                                                                            0 ? (
                                                                            <OverlayTrigger
                                                                              trigger="hover"
                                                                              placement="bottom"
                                                                              overlay={
                                                                                popoverPartialOrder
                                                                              }
                                                                            >
                                                                              <img
                                                                                className="me-2"
                                                                                width={28}
                                                                                height={
                                                                                  21
                                                                                }
                                                                                src={
                                                                                  IMAGES.PartialTruckTwo
                                                                                }
                                                                                alt=""
                                                                              />
                                                                            </OverlayTrigger>
                                                                          ) : (
                                                                            <></>
                                                                          )}
                                                                          <span className="text-dark d-block">
                                                                            {Method.convertDateToDDMMYYYYHOURS(
                                                                              orderVal._createdAt
                                                                            )}
                                                                          </span>
                                                                        </div>
                                                                        <span className="text-gray d-block">
                                                                          {
                                                                            orderVal.refKey
                                                                          }
                                                                        </span>
                                                                      </div>
                                                                    </div>
                                                                  </td>
                                                                ) : (
                                                                  <td className="fs-15 fw-500">
                                                                    <div className="d-flex align-items-start flex-column">
                                                                      <div className="d-flex flex-row">
                                                                        {orderVal.totalQuantities.reduce(
                                                                          (
                                                                            total: number,
                                                                            item: any
                                                                          ) =>
                                                                            total +
                                                                            item.stockCount,
                                                                          0
                                                                        ) >
                                                                          orderVal.mProcessedQuantities &&
                                                                        orderVal.mProcessedQuantities !=
                                                                          0 ? (
                                                                          <OverlayTrigger
                                                                            trigger="hover"
                                                                            placement="bottom-start"
                                                                            overlay={
                                                                              popoverPartialOrder
                                                                            }
                                                                          >
                                                                            <img
                                                                              className="me-2"
                                                                              width={28}
                                                                              height={21}
                                                                              src={
                                                                                IMAGES.PartialTruckTwo
                                                                              }
                                                                              alt=""
                                                                            />
                                                                          </OverlayTrigger>
                                                                        ) : (
                                                                          <></>
                                                                        )}
                                                                        <span className="text-dark d-block">
                                                                          {Method.convertDateToDDMMYYYYHOURS(
                                                                            orderVal._createdAt
                                                                          )}
                                                                        </span>
                                                                      </div>
                                                                      <span className="text-gray d-block">
                                                                        {orderVal.refKey}
                                                                      </span>
                                                                    </div>
                                                                  </td>
                                                                )} */}
                                <td className="fs-15 fw-500">
                                  <div className="d-flex align-items-start flex-column">
                                    <span className="text-dark d-block">
                                      {Method.convertDateToDDMMYYYYHOURS(
                                        orderVal._createdAt
                                      )}
                                    </span>
                                    <span className="text-gray d-block">
                                      {orderVal.refKey}
                                    </span>
                                  </div>
                                </td>
                                <td>
                                  <span className="fs-15 fw-500 d-block">
                                    {orderVal.customer.name}
                                  </span>
                                </td>
                                {/* {currentUser?.role.length ? (
                                  currentUser?.role.some(
                                    (obj: any) =>
                                      obj.type !== Manager ||
                                      obj.type !== ServiceMember
                                  ) ? (
                                    <></>
                                  ) : (
                                    <td>
                                      <span className="badge badge-light border-r23px p-4 fs-15 fw-600 text-dark">
                                        {orderVal.placedBy.type ===
                                        OrderPlacedByCustomer
                                          ? "Customer"
                                          : orderVal.placedBy.user.name}
                                      </span>
                                    </td>
                                  )
                                ) : (
                                  <></>
                                )} */}
                                {/* <td>
                                  <span className="fs-15 fw-500">
                                    {orderVal.mProcessedQuantities > 0
                                      ? orderVal.mOriginalOrderedQuantity -
                                        orderVal.mProcessedQuantities
                                      : orderVal.mOriginalOrderedQuantity}
                                    {" items"}
                                  </span>
                                </td> */}
                                <td>
                                  <span className="fs-15 fw-500 badge rounded-pill bg-light-secondary text-black p-4 h-10">
                                    {orderVal?.customer?.assignedTo?.name ?? "NA"}
                                  </span>
                                </td>
                                <td>
                                  <span className="fs-15 fw-500">
                                    {"TSh " +
                                      Method.getGeneralizedAmount(
                                        orderVal.payment.totalCharge
                                      )}
                                  </span>
                                </td>
                                <td>{getStatusBadge(orderVal.status)}</td>
                                <td className="text-end">
                                  {Method.hasPermission(
                                    OrdersDelivery,
                                    View,
                                    currentUser
                                  ) ? (
                                    <Button
                                      className="uw-btn uw-btn-primary fs-14 fw-600"
                                      onClick={() => {
                                        let currentScrollY = window.scrollY;
                                        setKey(
                                          listSalesRepOrdersDelivery.scrollPosition,
                                          currentScrollY
                                        );
                                        setKey(
                                          listSalesRepOrdersDelivery.search,
                                          search,
                                          false
                                        );
                                        setKey(
                                          listSalesRepOrdersDelivery.filterStartDate,
                                          startDate,
                                          false
                                        );
                                        setKey(
                                          listSalesRepOrdersDelivery.filterEndDate,
                                          endDate,
                                          false
                                        );
                                        navigate("/orders/order-details", {
                                          state: {
                                            id: orderVal._id,
                                            moduleName: OrdersDelivery,
                                          },
                                        });
                                      }}
                                    >
                                      View details
                                    </Button>
                                  ) : (
                                    <></>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </>
                        </tbody>
                      </table>
                    </div>
                  </Card.Body>
                </Card>
                {!loading && orders && orders.length ? (
                  <OrderPagination
                    totalRecords={totalRecords}
                    currentPage={page}
                    handleCurrentPage={handleCurrentPage}
                    handleNextPage={handleNextPage}
                    handlePreviousPage={handlePreviousPage}
                    handlePageLimit={handlePageLimit}
                    pageLimit={pageLimit}
                  />
                ) : (
                  <></>
                )}
              </>
            ) : (
              <>
                {startDate && endDate ? (
                  <div className="border border-r10px mb-6">
                    <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                      <span className="fs-18 fw-500">No Data Found</span>
                    </div>
                  </div>
                ) : (
                  <div className="border border-r10px mb-6">
                    <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                      <span className="fs-18 fw-500">
                        New orders / Unprocessed orders will be shown in this
                        section.
                      </span>
                    </div>
                  </div>
                )}
              </>
            )
          ) : (
            <div className="border border-r10px mb-6">
              <div className="d-flex justify-content-center text-center align-items-center min-h-160px px-3">
                <Loader loading={fetchLoader || loading} />
              </div>
            </div>
          )}
        </Col>
      </Row>
    </div>
  );
};
export default Orders;
